import { PropsWithChildren, ReactNode } from "react";
import { Box } from "@chakra-ui/react";

import {
  getCdnImageSrc,
  HStack,
  responsive,
  VStack,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  Image,
  Spacing,
  // call,
  customEvent,
} from "goi_common";

import MapLayout from "@/components/Map";
import { HEADER_HEIGHT } from "@/constants/layout";
import { AmenityObject, ParlorDetailProps } from "@/types/parlor";

import { MissParlorDetailBadgeCTASection } from "./components/MissParlorDetailBadgeCTASection";
import { MissParlorDetailBottomFixedModal } from "./components/MissParlorDetailBottomFixedModal";
import MissParlorDetailFuneralProcessTable from "./components/MissParlorDetailFuneralProcessTable";
import {
  MissParlorDetailHeader,
  MissParlorDetailHeaderMenuItems,
} from "./components/MissParlorDetailHeader";
import { MissParlorGoiHeader } from "./components/MissParlorGoiHeader";
import { PARLOR_FAMILY_MAIN_PATH } from "./constants/event";
import { useGoiCall } from "@/hooks/useGoiCall";

interface MissUnderstandingParlorDetailContinerProps {
  defaultParlor: ParlorDetailProps;
}

export const MissUnderstandingParlorDetailContiner = ({
  defaultParlor,
}: MissUnderstandingParlorDetailContinerProps) => {
  const {
    id,
    companyname: companyName,
    managed_type: managedType,
    address,
    sido,
    sigungu,
    amenities,
    traffpublic: traffPublic,
  } = defaultParlor;

  const HEADER_HREF_AND_TEXT: MissParlorDetailHeaderMenuItems = {
    "장례식장 안내": {
      href: "information",
      trigger_and_target: "nav-info",
    },
    "장례 견적": {
      href: `/match/parlor-requests/${id}/`,
      trigger_and_target: "nav-uv_mtch_rqst_prlr",
    },
    "장례 절차": {
      href: "funeral-process",
      trigger_and_target: "nav-fune-proc",
    },
    "교통편 안내": {
      href: "roadmap",
      trigger_and_target: "nav-road",
    },
  };

  return (
    <VStack
      width="100%"
      alignItems="center"
      css={{
        fontFamily: "NanumGothic",
      }}
    >
      <MissParlorGoiHeader />
      <MissParlorDetailHeader
        companyName={companyName}
        menuItems={HEADER_HREF_AND_TEXT}
      />

      <VStack
        as="section"
        css={responsive({
          width: "100%",
          maxWidth: {
            base: "480px",
            lg: "none",
          },
          position: "relative",
        })}
      >
        <ParlorDetailHeroSection />
        <MissParlorDetailBadgeCTASection companyName={companyName} />
      </VStack>
      <VStack
        width="100%"
        css={responsive({
          width: "100%",
          maxWidth: {
            base: "480px",
            lg: "720px",
          },
        })}
      >
        <ParlorDetailInformationSection
          companyName={companyName}
          sido={sido}
          sigungu={sigungu}
          managedType={managedType}
          amenities={amenities}
          hash={HEADER_HREF_AND_TEXT["장례식장 안내"].href}
        />

        <ParlorDetailTrafficSection
          companyName={companyName}
          address={address}
          traffPublic={traffPublic}
          parlor={defaultParlor}
          hash={HEADER_HREF_AND_TEXT["교통편 안내"].href}
        />

        <ParlorDetailFuneralProcessSection
          hash={HEADER_HREF_AND_TEXT["장례 절차"].href}
        />
        <ParlorDetailBinsoSection />
      </VStack>
      <Spacing gap={230} />

      <MissParlorDetailBottomFixedModal />
    </VStack>
  );
};

const ParlorDetailHeroSection = () => {
  const { goiCall } = useGoiCall();

  return (
    <section>
      <Box
        css={responsive({
          position: "relative",
          overflow: "hidden",
          width: "100%",
          height: {
            base: "342px",
            lg: "540px",
          },
        })}
      >
        <Image
          src={getCdnImageSrc(
            "parlor-platform/hero-flower-section-background.png"
          )}
          alt="flower-background"
          css={{ objectFit: "cover", minHeight: "100%" }}
        />
        <VStack
          className="head_28_r lg_head_48_r white"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
          css={responsive({
            top: 0,
            fontFamily: "Nanum Myeongjo",
            position: "absolute",
            textAlign: "center",
            paddingBottom: {
              lg: "124px",
            },
          })}
        >
          유가족의 슬픔을
          <br />
          함께하는 마음으로
        </VStack>
      </Box>

      <Box
        position="relative"
        cursor="pointer"
        css={responsive({
          display: {
            lg: "none",
          },
        })}
        onClick={() => {
          customEvent({
            newGtm: {
              path: PARLOR_FAMILY_MAIN_PATH,
              trigger_and_target: "goi_call",
            },
          });

          goiCall();
        }}
      >
        <Image
          src={getCdnImageSrc("parlor-platform/call-banner.png")}
          alt="call-banner"
        />
      </Box>
    </section>
  );
};

interface ParlorDetailInformationSectionProps {
  companyName: string;
  sido?: string;
  sigungu?: string;
  managedType?: string;
  amenities: AmenityObject;
  hash: string;
}
const ParlorDetailInformationSection = ({
  companyName,
  sido = "",
  sigungu = "",
  managedType = "",
  amenities,
  hash,
}: ParlorDetailInformationSectionProps) => {
  const sidoName = FUNERAL_SIDO.get(Number(sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(sido))?.get(Number(sigungu));

  const headingText = `${companyName} 안내`;
  const informationText = `${companyName}은\n${sidoName} ${sigunguName}에 위치한\n${managedType}장례식장입니다.`;

  return (
    <MissParlorDetailSection as="h1" title={headingText} hash={hash}>
      <p
        className="body_16_r lg_body_18_r"
        css={responsive({
          whiteSpace: {
            base: "pre-wrap",
            lg: "normal",
          },
        })}
      >
        {informationText}
      </p>
      <ParlorDetailAmenitySection amenities={amenities} />
    </MissParlorDetailSection>
  );
};

interface ParlorDetailAmenitySectionProps {
  amenities: AmenityObject;
}

const ParlorDetailAmenitySection = ({
  amenities,
}: ParlorDetailAmenitySectionProps) => {
  const {
    has_mealroom, // 식당
    has_super, // 매점
    has_facilities_for_impaired, // 장애인 편의시설
    has_parkinglot, // 주가 가능
    has_waitroom, // 유족대기실
    parkcnt, // 주차 가능 대수
  } = amenities;

  const amenityItemText: Record<string, string> = {
    has_mealroom: "식당",
    has_super: "매점",
    has_facilities_for_impaired: "장애인 편의시설",
    has_parkinglot: `주차 ${parkcnt}대`,
    has_waitroom: "유족대기실",
  };

  const validAmenityList = Object.entries({
    has_mealroom,
    has_super,
    has_facilities_for_impaired,
    has_parkinglot,
    has_waitroom,
  })
    .filter(([_, value]) => value)
    .map(([key, _]) => amenityItemText[key]);

  return (
    <section css={{ margin: "16px 0 0" }}>
      <h2
        className="subtitle_16_b lg_subtitle_18_sb"
        css={{ marginBottom: "8px" }}
      >
        편의시설
      </h2>
      <ul
        className="lg_subtitle_18_r"
        css={{
          listStyle: "inherit",
          paddingLeft: "20px",
        }}
      >
        {validAmenityList.map((value) => (
          <li key={value}>{value}</li>
        ))}
      </ul>
    </section>
  );
};

interface MissParlorDetailSectionProps {
  title: ReactNode;
  hash?: string;
  as?: any;
}

const MissParlorDetailSection = ({
  hash,
  title,
  as = "h2",
  children,
}: PropsWithChildren<MissParlorDetailSectionProps>) => {
  return (
    <Box as="section" margin="24px 20px">
      <Box
        id={hash}
        as={as}
        className="subtitle_21_b lg_head_24_b"
        css={{ marginBottom: "8px", scrollMarginTop: HEADER_HEIGHT }}
      >
        {title}
      </Box>
      {children}
    </Box>
  );
};
interface ParlorDetailTrafficSectionProps {
  companyName: string;
  address: string;
  traffPublic?: string;
  parlor: ParlorDetailProps;
  hash: string;
}

const ParlorDetailTrafficSection = ({
  companyName,
  address,
  traffPublic,
  parlor,
  hash,
}: ParlorDetailTrafficSectionProps) => {
  return (
    <MissParlorDetailSection title="교통편 안내" hash={hash}>
      {/* <div css={{ position: "relative", width: "100%" }}>
        <div
          className="subtitle_16_b lg_subtitle_21_b"
          css={responsive({
            position: "absolute",
            left: {
              base: "20px",
              lg: "45px",
            },
            top: {
              base: "20px",
              lg: "45px",
            },

            zIndex: "13",
            fontFamily: "Nanum Myeongjo",
          })}
        >
          {companyName}
        </div>
        <Image
          css={{
            borderRadius: "8px",
          }}
          src={getCdnImageSrc("parlor-platform/traffic-section-banner.png")}
          alt="오시는길 안내"
        />
      </div> */}

      <h3 className="body_14_m lg_body_16_m" css={{ margin: "10px 0 24px" }}>
        {address}
      </h3>

      <MapLayout parlor={parlor} height="250px" />

      <section>
        <HStack gap={15} margin={"20px 0 0"}>
          <div css={{ width: "14px", minWidth: "14px" }}>
            <Image
              width="100%"
              src={getCdnImageSrc("parlor-platform/address-rectangle.jpg")}
              alt=""
            />
          </div>

          <VStack gap={10}>
            <h3
              className="subtitle_16_sb lg_subtitle_18_sb"
              css={{
                color: "#7C2D00",
              }}
            >
              대중교통 이용 시
            </h3>
            <p className="caption_12_m lg_body_14_m">
              {traffPublic || "지하철 또는 버스 이용"}
            </p>
          </VStack>
        </HStack>
      </section>
    </MissParlorDetailSection>
  );
};

interface ParlorDetailFuneralProcessSectionProps {
  hash: string;
}

const ParlorDetailFuneralProcessSection = ({
  hash,
}: ParlorDetailFuneralProcessSectionProps) => {
  return (
    <MissParlorDetailSection title="장례 절차" hash={hash}>
      <MissParlorDetailFuneralProcessTable />
    </MissParlorDetailSection>
  );
};

const ParlorDetailBinsoSection = () => {
  return (
    <MissParlorDetailSection title="빈소 현황">
      {/* {TODO: 독사운드 처럼 이하늘에서 빈소가져와서 보여주고 출처도 남기기 } */}
      <Box position="relative" width="100%" height="160px">
        <Image
          src={getCdnImageSrc("parlor-platform/binso-message.png")}
          alt="flower-background"
        />
      </Box>
    </MissParlorDetailSection>
  );
};
